import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Text } from '@/hen-ui/atomics/Text';
import { RadioItemLabelContext } from '@/hen-ui/atomics/Radio/radio-label-context';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Flex } from '@/hen-ui/composites/Flex';
import { Tooltip, type TooltipProps } from '@/hen-ui/composites/Tooltip';
import { cn } from '@/hen-ui/utils';

import {
  ratingStarFillVariants,
  ratingStarStrokeVariants,
  ratingStarVariants,
  type RatingStarScoreSize,
  type RatingStarTheme,
  type RatingStarTone,
} from './rating-star.variants';

const MAX_STARS = 5;

/** Where the numeric score sits relative to the stars. */
export type RatingScorePosition = 'before' | 'after';

export interface RatingStarProps {
  /**
   * Rating used for fill (and numeric display when shown).
   * Missing or non-finite values are treated as `0`. Values are clamped to `0…5`.
   * Stars fill from the score as displayed (one decimal), so the number and
   * the stars always agree.
   */
  rating?: number | null;
  /** Review count as a string. Parsed to an integer and shown as `(8,134)`. */
  ratingCount?: string;
  /**
   * When false, hide the numeric rating text but still render stars from rating.
   * Ignored when {@link singleStar} is true, which always shows the score.
   * Default: true
   */
  showRatingValue?: boolean;
  /**
   * Where the numeric score sits relative to the stars.
   * `before` places it at the start. `after` places it beside the stars.
   * Any other value falls back to `before`.
   * Default: `before`
   */
  scorePosition?: RatingScorePosition;
  /**
   * When true, renders one star instead of five, filled when the score shown is
   * above 0 and empty otherwise, and always shows the score beside it.
   * Default: false
   */
  singleStar?: boolean;
  /**
   * When false, hide the parenthesized review count even if ratingCount is set.
   * Default: true
   */
  showRatingCount?: boolean;
  /**
   * Appended after {@link ratingCount} in the group aria-label when a count is
   * present (e.g. `' reviews'` for surfaces that store a bare count).
   */
  ratingCountAriaSuffix?: string;
  /** Visual scale. Default: `default` */
  scoreSize?: RatingStarScoreSize;
  showRatingTooltip?: boolean;
  ratingTooltip?: string;
  tooltipSide?: TooltipProps['side'];
  /**
   * Copy color for rating + ratingCount. Stars ignore theme.
   * Default: `light`
   */
  theme?: RatingStarTheme;
  /**
   * Star fill and chrome. `default` keeps warning-gold classes. `theme` follows
   * the tenant theme, and stays gold when that color is unset.
   * Default: `default`
   */
  starTone?: RatingStarTone;
  /**
   * Merged onto both star layers after the default colors, so `text-*` restyles
   * the stroke of empty and filled stars alike.
   */
  starClassName?: string;
  /**
   * Merged onto the filled overlay only, after {@link starClassName}. Use
   * `fill-*` for the fill and `text-*` for the filled star's stroke.
   */
  starFillClassName?: string;
  className?: string;
}

/** Portion of the star at `index` that should render filled, 0 through 1. */
export function getStarFill(rating: number, index: number): number {
  return Math.round(Math.min(Math.max(rating - index, 0), 1) * 100) / 100;
}

/**
 * Fill for each star the display renders. A single star is filled only when
 * the score rounds to at least `0.1`, so it never sits beside `0.0`.
 */
export function getStarFills(rating: number, singleStar = false): number[] {
  if (singleStar) {
    return [Math.round(rating * 10) > 0 ? 1 : 0];
  }

  return Array.from({ length: MAX_STARS }, (_, index) =>
    getStarFill(rating, index),
  );
}

/** Always one decimal place (`"5.0"`, `"3.0"`, `"2.1"`). */
export function formatRatingValue(value: number): string {
  return value.toFixed(1);
}

/** Parse a review-count string (`"8134"`, `"8,134"`) into a non-negative integer. */
export function parseRatingCount(ratingCount: string): number | undefined {
  const normalized = ratingCount.replace(/,/g, '').trim();
  if (!/^\d+$/.test(normalized)) {
    return undefined;
  }

  const value = Number(normalized);

  if (!Number.isInteger(value) || value < 0) {
    return undefined;
  }

  return value;
}

interface StarProps {
  fill: number;
  iconClassName: string;
  starTone?: RatingStarTone;
  starClassName?: string;
  starFillClassName?: string;
}

function Star({
  fill,
  iconClassName,
  starTone = 'default',
  starClassName,
  starFillClassName,
}: StarProps) {
  return (
    <Flex
      direction='row'
      align='center'
      gap='none'
      className='relative inline-flex shrink-0'
      data-star-fill={fill}
      aria-hidden
    >
      <Icon
        icon='star'
        size='sm'
        strokeWidth={1.5}
        className={cn(
          iconClassName,
          ratingStarStrokeVariants({ starTone }),
          starClassName,
        )}
        aria-hidden
      />
      {!!fill && (
        <Flex
          direction='row'
          align='center'
          gap='none'
          className='absolute inset-y-0 left-0 overflow-hidden'
          style={{ width: `${fill * 100}%` }}
        >
          <Icon
            icon='star'
            size='sm'
            strokeWidth={1.5}
            className={cn(
              iconClassName,
              ratingStarFillVariants({ starTone }),
              starClassName,
              starFillClassName,
            )}
            aria-hidden
          />
        </Flex>
      )}
    </Flex>
  );
}

function RatingInfoIcon({
  showRatingTooltip = false,
  ratingTooltip,
  tooltipSide,
  triggerClassName,
}: Pick<
  RatingStarProps,
  'showRatingTooltip' | 'ratingTooltip' | 'tooltipSide'
> & { triggerClassName: string }) {
  const tooltipContent = ratingTooltip?.trim();

  if (!showRatingTooltip || !tooltipContent) {
    return null;
  }

  return (
    <Tooltip content={tooltipContent} side={tooltipSide}>
      <Button
        type='button'
        variant='iconOnly'
        size='flush'
        className={cn('shrink-0', triggerClassName)}
        aria-label='Info about rating'
      >
        <Icon icon='info' strokeWidth={1.5} aria-hidden />
      </Button>
    </Tooltip>
  );
}

/**
 * RatingStar — accessible, props-only star rating for HEN product surfaces.
 *
 * Layout: numeric value (before the stars by default) + stars + optional
 * `(ratingCount)`. `scorePosition="after"` puts the number beside the stars.
 * The score always shows one decimal place. Partial fill uses the same
 * overflow-clip approach as PartnerStarRating. `singleStar` swaps the row
 * for one star and always shows the score.
 */
export const RatingStar: React.FC<RatingStarProps> = ({
  rating,
  ratingCount,
  showRatingValue = true,
  scorePosition = 'before',
  singleStar = false,
  showRatingCount = true,
  ratingCountAriaSuffix = '',
  scoreSize = 'default',
  showRatingTooltip = false,
  ratingTooltip,
  tooltipSide,
  theme = 'light',
  starTone = 'default',
  starClassName = '',
  starFillClassName = '',
  className,
}) => {
  const inRadioItemLabel = React.useContext(RadioItemLabelContext);
  const safeRating = rating != null && Number.isFinite(rating) ? rating : 0;
  const displayRating = Math.min(Math.max(safeRating, 0), MAX_STARS);
  const formattedRating = formatRatingValue(displayRating);
  const starFills = getStarFills(Number(formattedRating), singleStar);
  const scoreAfterStars = scorePosition === 'after';
  const showScore = singleStar || showRatingValue;
  const parsedRatingCount =
    ratingCount != null ? parseRatingCount(ratingCount) : undefined;
  const formattedReviews =
    showRatingCount && parsedRatingCount != null
      ? parsedRatingCount.toLocaleString('en-US')
      : undefined;

  const groupAriaLabel = ratingCount
    ? `${formattedRating} out of ${MAX_STARS} stars, ${ratingCount}${ratingCountAriaSuffix}`
    : `${formattedRating} out of ${MAX_STARS} stars`;

  const { icon, triggerIcon, copy, text } = ratingStarVariants({
    theme,
    scoreSize,
  });

  const score = showScore ? (
    <Text variant={text} weight='bold' tag='span' className={copy}>
      {formattedRating}
    </Text>
  ) : null;

  const stars = (
    <Flex direction='row' align='center' gap='min' aria-hidden>
      {starFills.map((fill, index) => (
        <Star
          key={index}
          fill={fill}
          iconClassName={icon}
          starTone={starTone}
          starClassName={starClassName}
          starFillClassName={starFillClassName}
        />
      ))}
    </Flex>
  );

  return (
    <Flex
      direction='row'
      align='center'
      gap='xxs'
      role='group'
      className={cn('shrink-0', className)}
      aria-label={groupAriaLabel}
      data-hen-id={COMPONENT_IDS.Rating}
    >
      {scoreAfterStars ? null : score}
      {stars}
      {scoreAfterStars ? score : null}
      {!!formattedReviews && (
        <Text variant={text} weight='regular' tag='span' className={copy}>
          {`(${formattedReviews})`}
        </Text>
      )}
      <RatingInfoIcon
        showRatingTooltip={showRatingTooltip && !inRadioItemLabel}
        ratingTooltip={ratingTooltip}
        tooltipSide={tooltipSide}
        triggerClassName={triggerIcon}
      />
    </Flex>
  );
};

RatingStar.displayName = 'RatingStar';
